import { type PublicBuildingState, type PublicCityState, type PublicCityscapeApp, type PublicPortfolioMetricsSlice, buildPublicCityState } from "@point/core";

import { useQuery } from "convex/react";
import { anyApi, type FunctionReference } from "convex/server";
import { useMemo } from "react";

// We deliberately don't import from `convex/functions/_generated/api.js`
// because that path lives outside this Vite project's root, and Vercel's
// build only installs `node_modules` inside `point-public/` — the
// generated file would then fail to resolve `convex/server`.
//
// At runtime Convex's `api` is just `anyApi`; the typed bindings only
// matter at compile time. We narrow the relevant signatures by hand.
type CityscapeQuery = FunctionReference<"query", "public", { onlyLive?: boolean }, PublicCityscapeApp[]>;
type StatsQuery = FunctionReference<
	"query",
	"public",
	Record<string, never>,
	{
		total: number;
		live: number;
		inReview: number;
		building: number;
		new: number;
	}
>;
const cityscapeApi = anyApi.cityscape as unknown as {
	cityscape: CityscapeQuery;
	stats: StatsQuery;
};
const cityscapeRef = cityscapeApi.cityscape;

const EMPTY: PublicCityState & { loading: boolean } = {
	buildings: [],
	positionMap: new Map(),
	stats: { total: 0, live: 0, building: 0, newApps: 0 },
	loading: true,
};

export function useCityData(opts?: { onlyLive?: boolean; portfolioMetrics?: Map<string, PublicPortfolioMetricsSlice> }) {
	const apps = useQuery(cityscapeRef, { onlyLive: opts?.onlyLive });

	return useMemo(() => {
		if (!apps) return EMPTY;
		return {
			...buildPublicCityState(apps, {
				portfolioMetrics: opts?.portfolioMetrics,
			}),
			loading: false,
		};
	}, [apps, opts?.portfolioMetrics]);
}
